import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Mail, Send, Users, TrendingUp, AlertTriangle, RefreshCw,
  Zap, ArrowUpRight, Clock, CheckCircle2, XCircle, Eye, MousePointerClick,
  FlaskConical, Trophy
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const CAMPAIGN_LABELS = {
  upgrade: 'Trial Upgrade',
  quarterly_upgrade: 'Quarterly Upgrade',
  reengagement: 'Re-engagement',
};

const CAMPAIGN_COLORS = {
  upgrade: '#10b981',
  quarterly_upgrade: '#3b82f6',
  reengagement: '#f59e0b',
};

const PIE_COLORS = ['#10b981', '#3b82f6', '#f59e0b'];

export const EngagementDashboard = ({ token }) => {
  const [data, setData] = useState(null);
  const [abData, setAbData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [triggerResult, setTriggerResult] = useState(null);

  const headers = { headers: { Authorization: `Bearer ${token}` } };

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, abRes] = await Promise.all([
        axios.get(`${API}/api/mybookie/admin/engagement/stats`, headers),
        axios.get(`${API}/api/mybookie/admin/engagement/ab-results`, headers),
      ]);
      setData(statsRes.data);
      setAbData(abRes.data);
    } catch (err) {
      console.error("Engagement dashboard fetch error:", err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchStats();
  }, [token, fetchStats]);

  const triggerCampaigns = async () => {
    setTriggering(true);
    setTriggerResult(null);
    try {
      const res = await axios.post(`${API}/api/mybookie/admin/engagement/trigger`, {}, headers);
      setTriggerResult(res.data);
      fetchStats();
    } catch (err) {
      console.error("Engagement trigger error:", err);
      setTriggerResult({ status: 'error' });
    } finally {
      setTriggering(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="engagement-loading">
        <RefreshCw className="w-5 h-5 animate-spin text-[#0A2B1D]" />
        <span className="ml-2 text-sm text-[#5C6661]">Loading engagement data...</span>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12 text-[#5C6661]" data-testid="engagement-error">
        <AlertTriangle className="w-10 h-10 mx-auto mb-2 text-[#B4C9BE]" />
        <p className="text-sm">Failed to load engagement data.</p>
      </div>
    );
  }

  const { by_campaign, daily_breakdown, funnel, recent_sends, tracking } = data;

  const pieData = Object.entries(by_campaign)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: CAMPAIGN_LABELS[k] || k, value: v }));

  // Only show last 14 days on the chart for readability
  const chartData = (daily_breakdown || []).slice(-14);

  const openRate = tracking?.open_rate ?? 0;
  const clickRate = tracking?.click_rate ?? 0;

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white border border-[#E6E8E6] rounded p-2.5 shadow-lg text-xs">
        <p className="font-medium text-[#111614] mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={p.name} style={{ color: p.color }}>{p.name}: {p.value}</p>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-5" data-testid="engagement-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2.5">
          <Zap className="w-5 h-5 text-[#0A2B1D]" />
          <h2 className="text-lg font-semibold text-[#111614]">Engagement Campaigns</h2>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchStats}
            disabled={loading}
            data-testid="engagement-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={triggerCampaigns}
            disabled={triggering}
            className="bg-[#0A2B1D] hover:bg-[#0A2B1D]/90 text-white"
            data-testid="engagement-trigger-btn"
          >
            <Send className={`w-3.5 h-3.5 mr-1.5 ${triggering ? 'animate-spin' : ''}`} />
            {triggering ? 'Running...' : 'Run Campaigns Now'}
          </Button>
        </div>
      </div>

      {/* Trigger result banner */}
      {triggerResult && (
        <div
          className={`flex items-center gap-2 px-4 py-2.5 rounded text-sm ${
            triggerResult.status === 'ok'
              ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}
          data-testid="trigger-result-banner"
        >
          {triggerResult.status === 'ok' ? (
            <>
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              <span>
                Campaigns complete: {triggerResult.upgrade_emails_sent} upgrade +{' '}
                {triggerResult.reengagement_emails_sent} re-engagement emails sent.
              </span>
            </>
          ) : (
            <>
              <XCircle className="w-4 h-4 shrink-0" />
              <span>Failed to run campaigns.</span>
            </>
          )}
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-[#0A2B1D]" data-testid="engagement-total-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-[#5C6661]">Total Emails Sent</span>
              <Mail className="w-4 h-4 text-[#0A2B1D]" />
            </div>
            <p className="text-2xl font-bold text-[#111614]">{data.total_sent}</p>
            <p className="text-xs text-[#8A948F] mt-1">{data.total_failed} failed</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-emerald-500" data-testid="engagement-7d-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-[#5C6661]">Last 7 Days</span>
              <TrendingUp className="w-4 h-4 text-emerald-500" />
            </div>
            <p className="text-2xl font-bold text-[#111614]">{data.last_7_days}</p>
            <p className="text-xs text-[#8A948F] mt-1">emails sent</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-500" data-testid="engagement-funnel-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-[#5C6661]">User Funnel</span>
              <Users className="w-4 h-4 text-amber-500" />
            </div>
            <p className="text-2xl font-bold text-[#111614]">{funnel?.trial_free || 0}</p>
            <p className="text-xs text-[#8A948F] mt-1">
              trial/free of {funnel?.total_users || 0} &middot; {funnel?.paid_active || 0} paid
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500" data-testid="engagement-30d-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-[#5C6661]">Last 30 Days</span>
              <ArrowUpRight className="w-4 h-4 text-blue-500" />
            </div>
            <p className="text-2xl font-bold text-[#111614]">{data.last_30_days}</p>
            <p className="text-xs text-[#8A948F] mt-1">emails sent</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-violet-500" data-testid="engagement-open-rate-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-[#5C6661]">Open Rate</span>
              <Eye className="w-4 h-4 text-violet-500" />
            </div>
            <p className="text-2xl font-bold text-[#111614]">{openRate}%</p>
            <p className="text-xs text-[#8A948F] mt-1">{tracking?.total_opened || 0} of {tracking?.total_tracked || 0} opened</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-rose-500" data-testid="engagement-click-rate-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-[#5C6661]">Click Rate</span>
              <MousePointerClick className="w-4 h-4 text-rose-500" />
            </div>
            <p className="text-2xl font-bold text-[#111614]">{clickRate}%</p>
            <p className="text-xs text-[#8A948F] mt-1">{tracking?.total_clicked || 0} of {tracking?.total_tracked || 0} clicked</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid md:grid-cols-3 gap-5">
        {/* Daily Activity - 2 cols */}
        <Card className="md:col-span-2" data-testid="engagement-daily-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#0A2B1D]" />
              Email Activity (Last 14 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {chartData.some(d => d.total > 0) ? (
              <div className="h-[220px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E6E8E6" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10, fill: '#8A948F' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={v => v.slice(5)}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: '#8A948F' }}
                      axisLine={false}
                      tickLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="upgrade" name="Trial Upgrade" fill="#10b981" stackId="a" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="quarterly" name="Quarterly Upgrade" fill="#3b82f6" stackId="a" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="reengagement" name="Re-engagement" fill="#f59e0b" stackId="a" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-[#8A948F] text-sm">
                No email activity in the last 14 days
              </div>
            )}
          </CardContent>
        </Card>

        {/* Campaign Breakdown Pie */}
        <Card data-testid="engagement-campaign-breakdown">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#0A2B1D]" />
              Campaign Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <div className="h-[220px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="45%"
                      innerRadius={45}
                      outerRadius={70}
                      dataKey="value"
                      paddingAngle={3}
                      label={({ name, value }) => `${value}`}
                    >
                      {pieData.map((_, idx) => (
                        <Cell key={`pie-${idx}`} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [value, name]} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[220px] flex flex-col items-center justify-center text-[#8A948F]">
                <Mail className="w-8 h-8 mb-2 opacity-40" />
                <p className="text-sm">No campaigns sent yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Campaign Type Stats with Open/Click rates */}
      <div className="grid sm:grid-cols-3 gap-4">
        {Object.entries(by_campaign).map(([key, count]) => {
          const campTracking = tracking?.by_campaign?.[key] || {};
          return (
            <div
              key={key}
              className="bg-[#F6F5F2] rounded p-3.5"
              data-testid={`campaign-stat-${key}`}
            >
              <div className="flex items-center gap-2.5 mb-2">
                <div
                  className="w-3 h-3 rounded-full shrink-0"
                  style={{ backgroundColor: CAMPAIGN_COLORS[key] || '#9ca3af' }}
                />
                <p className="text-sm font-medium text-[#111614] truncate">
                  {CAMPAIGN_LABELS[key] || key}
                </p>
              </div>
              <p className="text-xs text-[#5C6661] mb-2">{count} emails sent</p>
              <div className="flex gap-3 text-xs">
                <div className="flex items-center gap-1">
                  <Eye className="w-3 h-3 text-violet-500" />
                  <span className="text-[#5C6661]">{campTracking.open_rate || 0}% opened</span>
                </div>
                <div className="flex items-center gap-1">
                  <MousePointerClick className="w-3 h-3 text-rose-500" />
                  <span className="text-[#5C6661]">{campTracking.click_rate || 0}% clicked</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent Sends Table */}
      <Card data-testid="engagement-recent-sends">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Send className="w-4 h-4 text-[#0A2B1D]" />
            Recent Sends
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recent_sends?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#E6E8E6]">
                    <th className="text-left py-2 pr-4 text-xs font-medium text-[#5C6661]">Email</th>
                    <th className="text-left py-2 pr-4 text-xs font-medium text-[#5C6661]">Campaign</th>
                    <th className="text-left py-2 pr-3 text-xs font-medium text-[#5C6661]">Opened</th>
                    <th className="text-left py-2 pr-3 text-xs font-medium text-[#5C6661]">Clicked</th>
                    <th className="text-left py-2 text-xs font-medium text-[#5C6661]">Sent</th>
                  </tr>
                </thead>
                <tbody>
                  {recent_sends.map((send, i) => (
                    <tr key={send.tracking_id || `send-${i}`} className="border-b border-[#F0F0F0] last:border-0">
                      <td className="py-2 pr-4 text-[#111614] truncate max-w-[180px]">{send.email}</td>
                      <td className="py-2 pr-4">
                        <Badge
                          variant="secondary"
                          className="text-xs font-normal"
                          style={{
                            backgroundColor: `${CAMPAIGN_COLORS[send.campaign] || '#9ca3af'}20`,
                            color: CAMPAIGN_COLORS[send.campaign] || '#9ca3af',
                          }}
                        >
                          {CAMPAIGN_LABELS[send.campaign] || send.campaign}
                        </Badge>
                      </td>
                      <td className="py-2 pr-3">
                        {send.opened ? (
                          <span className="inline-flex items-center gap-1 text-xs text-violet-600">
                            <Eye className="w-3 h-3" /> {send.open_count || 1}x
                          </span>
                        ) : (
                          <span className="text-xs text-[#B4C9BE]">&mdash;</span>
                        )}
                      </td>
                      <td className="py-2 pr-3">
                        {send.clicked ? (
                          <span className="inline-flex items-center gap-1 text-xs text-rose-600">
                            <MousePointerClick className="w-3 h-3" /> {send.click_count || 1}x
                          </span>
                        ) : (
                          <span className="text-xs text-[#B4C9BE]">&mdash;</span>
                        )}
                      </td>
                      <td className="py-2 text-xs text-[#8A948F] whitespace-nowrap">
                        {new Date(send.sent_at).toLocaleDateString('en-US', {
                          month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-[#8A948F] text-sm">
              No emails sent yet. Click "Run Campaigns Now" to trigger the first cycle.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Campaign Rules Info */}
      <Card className="border-[#E2ECE7] bg-[#F6F5F2]" data-testid="engagement-rules-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Zap className="w-4 h-4 text-emerald-600" />
            Campaign Rules
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="bg-white rounded p-3 border border-[#E6E8E6]">
              <div className="flex items-center gap-2 mb-1.5">
                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                <p className="text-sm font-medium text-[#111614]">Trial Upgrade</p>
              </div>
              <p className="text-xs text-[#5C6661] leading-relaxed">
                Free/trial users who haven't upgraded after 14 days. Emails every 3 days for 30 days. Revisit at day 90.
              </p>
            </div>
            <div className="bg-white rounded p-3 border border-[#E6E8E6]">
              <div className="flex items-center gap-2 mb-1.5">
                <div className="w-2 h-2 rounded-full bg-blue-500" />
                <p className="text-sm font-medium text-[#111614]">Quarterly Upgrade</p>
              </div>
              <p className="text-xs text-[#5C6661] leading-relaxed">
                Active paid members (Pro/Business) receive upgrade emails every 3 months suggesting the next tier.
              </p>
            </div>
            <div className="bg-white rounded p-3 border border-[#E6E8E6]">
              <div className="flex items-center gap-2 mb-1.5">
                <div className="w-2 h-2 rounded-full bg-amber-500" />
                <p className="text-sm font-medium text-[#111614]">Re-engagement</p>
              </div>
              <p className="text-xs text-[#5C6661] leading-relaxed">
                Paid users inactive 14+ days. Escalating: quick wins (14d), snapshot (21d), we miss you (30d).
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* A/B Test Results */}
      <ABTestResults abData={abData} />
    </div>
  );
};

const ABTestResults = ({ abData }) => {
  if (!abData || abData.total_ab_tested === 0) {
    return (
      <Card className="border-dashed border-[#D1D5DB]" data-testid="ab-test-empty">
        <CardContent className="p-6 text-center">
          <FlaskConical className="w-8 h-8 mx-auto mb-2 text-[#B4C9BE]" />
          <p className="text-sm font-medium text-[#5C6661]">A/B Testing</p>
          <p className="text-xs text-[#8A948F] mt-1">
            Subject line A/B tests run automatically. Results will appear after emails are sent with the new variant system.
          </p>
        </CardContent>
      </Card>
    );
  }

  const { overall, by_campaign, winner, subject_variants } = abData;

  const barData = [
    {
      metric: 'Open Rate',
      A: overall.A.open_rate,
      B: overall.B.open_rate,
    },
    {
      metric: 'Click Rate',
      A: overall.A.click_rate,
      B: overall.B.click_rate,
    },
  ];

  return (
    <Card data-testid="ab-test-results">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <FlaskConical className="w-4 h-4 text-[#0A2B1D]" />
            A/B Subject Line Testing
          </CardTitle>
          {winner && winner !== 'tie' && (
            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs font-normal" data-testid="ab-winner-badge">
              <Trophy className="w-3 h-3 mr-1" />
              Variant {winner} winning
            </Badge>
          )}
          {winner === 'tie' && (
            <Badge className="bg-gray-50 text-gray-600 border-gray-200 text-xs font-normal">Tied</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Overall A vs B comparison */}
        <div className="grid sm:grid-cols-2 gap-4">
          {['A', 'B'].map((v) => (
            <div
              key={v}
              className={`rounded-lg p-4 border ${
                winner === v
                  ? 'border-emerald-300 bg-emerald-50/50'
                  : 'border-[#E6E8E6] bg-white'
              }`}
              data-testid={`ab-variant-${v.toLowerCase()}-card`}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-[#111614]">
                  Variant {v}
                  {winner === v && <Trophy className="w-3.5 h-3.5 inline ml-1.5 text-emerald-600" />}
                </span>
                <span className="text-xs text-[#8A948F]">{overall[v].sent} sent</span>
              </div>
              <div className="flex gap-4">
                <div>
                  <p className="text-xs text-[#5C6661]">Open Rate</p>
                  <p className="text-lg font-bold text-[#111614]">{overall[v].open_rate}%</p>
                </div>
                <div>
                  <p className="text-xs text-[#5C6661]">Click Rate</p>
                  <p className="text-lg font-bold text-[#111614]">{overall[v].click_rate}%</p>
                </div>
                <div>
                  <p className="text-xs text-[#5C6661]">Opened</p>
                  <p className="text-lg font-bold text-[#111614]">{overall[v].opened}</p>
                </div>
                <div>
                  <p className="text-xs text-[#5C6661]">Clicked</p>
                  <p className="text-lg font-bold text-[#111614]">{overall[v].clicked}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* A vs B Bar Chart */}
        {(overall.A.sent > 0 || overall.B.sent > 0) && (
          <div className="h-[180px]" data-testid="ab-comparison-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barData} margin={{ top: 5, right: 20, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E6E8E6" />
                <XAxis dataKey="metric" tick={{ fontSize: 12, fill: '#5C6661' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#8A948F' }} axisLine={false} tickLine={false} unit="%" />
                <Tooltip formatter={(val) => `${val}%`} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="A" name="Variant A" fill="#0A2B1D" radius={[4, 4, 0, 0]} />
                <Bar dataKey="B" name="Variant B" fill="#6366f1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Subject Line Variants Reference */}
        {subject_variants?.length > 0 && (
          <div className="space-y-2" data-testid="ab-subject-variants">
            <p className="text-xs font-medium text-[#5C6661] uppercase tracking-wide">Subject Line Variants</p>
            {subject_variants.map((sv) => (
              <div key={sv.offer_key} className="bg-[#F6F5F2] rounded p-3 text-xs">
                <p className="font-medium text-[#111614] mb-1.5 capitalize">
                  {sv.offer_key.replace(/_/g, ' ')}
                </p>
                <div className="grid sm:grid-cols-2 gap-2">
                  <div className="flex gap-1.5">
                    <Badge variant="outline" className="shrink-0 text-[10px] px-1.5 py-0">A</Badge>
                    <span className="text-[#5C6661]">{sv.subject_a}</span>
                  </div>
                  <div className="flex gap-1.5">
                    <Badge variant="outline" className="shrink-0 text-[10px] px-1.5 py-0 border-indigo-300 text-indigo-600">B</Badge>
                    <span className="text-[#5C6661]">{sv.subject_b}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Per-campaign A/B breakdown */}
        {by_campaign && Object.keys(by_campaign).some(k =>
          by_campaign[k].A?.sent > 0 || by_campaign[k].B?.sent > 0
        ) && (
          <div data-testid="ab-by-campaign">
            <p className="text-xs font-medium text-[#5C6661] uppercase tracking-wide mb-2">By Campaign</p>
            <div className="grid sm:grid-cols-3 gap-3">
              {Object.entries(by_campaign).map(([key, variants]) => {
                if (variants.A.sent === 0 && variants.B.sent === 0) return null;
                return (
                  <div key={key} className="bg-[#F6F5F2] rounded p-3 text-xs">
                    <p className="font-medium text-[#111614] mb-2">
                      {CAMPAIGN_LABELS[key] || key}
                    </p>
                    <div className="space-y-1.5">
                      {['A', 'B'].map((v) => (
                        <div key={v} className="flex items-center justify-between">
                          <span className="text-[#5C6661]">Variant {v}</span>
                          <span className="font-medium text-[#111614]">
                            {variants[v].open_rate}% open · {variants[v].click_rate}% click
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EngagementDashboard;
